import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";
import { toast } from "sonner";
import { newDrawingId } from "../engine";

const MAX_HISTORY = 50;

// Per-symbol drawing objects with undo/redo and debounced persistence (PUT /api/drawings/sync).
export function useDrawings(symbol) {
  const [all, setAll] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const past = useRef([]);
  const future = useRef([]);
  const dirty = useRef(false);
  const timer = useRef(null);
  const symRef = useRef(symbol);
  const [, bump] = useState(0);

  const inflight = useRef(false);
  const pendingList = useRef(null);

  const flush = useCallback((sym, list) => {
    if (!dirty.current) return;
    if (inflight.current) { pendingList.current = { sym, list }; return; } // serialize: one sync at a time
    dirty.current = false;
    inflight.current = true;
    api.put("/drawings/sync", { symbol: sym, drawings: list.map(toApi) })
      .catch(() => { dirty.current = true; toast.error("Could not save drawings — retries on your next change"); })
      .finally(() => {
        inflight.current = false;
        const p = pendingList.current;
        pendingList.current = null;
        if (p) { dirty.current = true; flush(p.sym, p.list); }
      });
  }, []);

  useEffect(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    flush(symRef.current, all);
    symRef.current = symbol;
    past.current = []; future.current = []; dirty.current = false;
    setSelectedId(null); setLoaded(false); setAll([]);
    let alive = true;
    api.get("/drawings", { params: { symbol } })
      .then(({ data }) => { if (alive) setAll(data.drawings.map(fromApi)); })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [symbol]); // eslint-disable-line

  useEffect(() => {
    if (!dirty.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(symRef.current, all), 600);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [all, flush]);

  const commit = useCallback((updater) => {
    setAll((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (next === prev) return prev;
      past.current = [...past.current.slice(-MAX_HISTORY + 1), prev];
      future.current = [];
      dirty.current = true;
      return next;
    });
    bump((n) => n + 1);
  }, []);

  const undo = useCallback(() => {
    if (!past.current.length) return;
    setAll((cur) => { const prev = past.current.pop(); future.current.push(cur); dirty.current = true; return prev; });
    setSelectedId(null); bump((n) => n + 1);
  }, []);
  const redo = useCallback(() => {
    if (!future.current.length) return;
    setAll((cur) => { const nxt = future.current.pop(); past.current.push(cur); dirty.current = true; return nxt; });
    bump((n) => n + 1);
  }, []);

  const actions = useMemo(() => ({
    add: (d) => { commit((p) => [...p, d]); setSelectedId(d.id); },
    replaceAll: (list) => commit(list),
    update: (id, patch) => commit((p) => p.map((d) => (d.id === id ? { ...d, ...(typeof patch === "function" ? patch(d) : patch) } : d))),
    remove: (id) => { commit((p) => p.filter((d) => d.id !== id)); setSelectedId((s) => (s === id ? null : s)); },
    duplicate: (id) => {
      let nid = null;
      commit((p) => {
        const src = p.find((d) => d.id === id);
        if (!src) return p;
        const shift = (src.points[1]?.time ?? src.points[0].time) - src.points[0].time || 0;
        nid = newDrawingId();
        return [...p, { ...src, id: nid, locked: false, points: src.points.map((pt) => ({ ...pt, time: pt.time + (shift || 30) })) }];
      });
      if (nid) setSelectedId(nid);
    },
    clear: () => { commit([]); setSelectedId(null); },
    undo, redo,
  }), [commit, undo, redo]);

  return { drawings: all, loaded, selectedId, setSelectedId, canUndo: past.current.length > 0, canRedo: future.current.length > 0, ...actions };
}

const toApi = (d) => ({ id: d.id, type: d.type, points: d.points, style: d.style, timeframe: d.timeframe ?? null, visible: d.visible, locked: d.locked, text: d.text ?? null, meta: d.meta ?? null });
const fromApi = (d) => ({ id: d.id, type: d.type, symbol: d.symbol, points: d.points, style: d.style, timeframe: d.timeframe ?? null, visible: d.visible, locked: d.locked, text: d.text ?? undefined, meta: d.meta ?? undefined });
